import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { attendanceAPI } from '../../services/api';
import { getErrorMessage } from '../../utils/errorHandler';

export const fetchAttendance = createAsyncThunk('attendance/fetchAll', async (params, { rejectWithValue }) => {
  try {
    const response = await attendanceAPI.fetchAttendance(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch attendance'));
  }
});

export const markAttendance = createAsyncThunk('attendance/mark', async (data, { rejectWithValue }) => {
  try {
    const response = await attendanceAPI.markAttendance(data);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to mark attendance'));
  }
});

export const markBulkAttendance = createAsyncThunk('attendance/markBulk', async (data, { rejectWithValue }) => {
  try {
    const response = await attendanceAPI.markBulkAttendance(data);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to mark bulk attendance'));
  }
});

export const getAttendanceReport = createAsyncThunk('attendance/report', async (params, { rejectWithValue }) => {
  try {
    const response = await attendanceAPI.getAttendanceReport(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch report'));
  }
});

export const getStudentAttendance = createAsyncThunk('attendance/student', async ({ id, params }, { rejectWithValue }) => {
  try {
    const response = await attendanceAPI.getStudentAttendance(id, params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch student attendance'));
  }
});

export const getStudentAttendanceSummary = createAsyncThunk('attendance/studentSummary', async ({ id, params }, { rejectWithValue }) => {
  try {
    const response = await attendanceAPI.getStudentAttendanceSummary(id, params);
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch student attendance summary'));
  }
});

export const getAttendanceStats = createAsyncThunk('attendance/stats', async (params, { rejectWithValue }) => {
  try {
    const response = await attendanceAPI.getAttendanceStats(params);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch stats'));
  }
});

const initialState = {
  records: [],
  todayRecords: [],
  currentStudentAttendance: [],
  studentSummary: null,
  report: null,
  stats: null,
  loading: false,
  error: null,
};

const attendanceSlice = createSlice({
  name: 'attendance',
  initialState,
  reducers: {
    clearAttendanceError: (state) => { state.error = null; },
    addRealtimeAttendance: (state, action) => {
      state.todayRecords.unshift(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAttendance.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAttendance.fulfilled, (state, action) => {
        state.loading = false;
        state.records = action.payload.data || action.payload;
      })
      .addCase(fetchAttendance.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(markAttendance.fulfilled, (state, action) => {
        state.records.unshift(action.payload);
        state.todayRecords.unshift(action.payload);
      })
      .addCase(markBulkAttendance.fulfilled, (state, action) => {
        const newRecords = Array.isArray(action.payload) ? action.payload : [action.payload];
        state.records = [...newRecords, ...state.records];
        state.todayRecords = [...newRecords, ...state.todayRecords];
      })
      .addCase(getAttendanceReport.fulfilled, (state, action) => {
        state.report = action.payload;
      })
      .addCase(getStudentAttendance.fulfilled, (state, action) => {
        state.currentStudentAttendance = action.payload.data || action.payload;
      })
      .addCase(getStudentAttendanceSummary.fulfilled, (state, action) => {
        state.studentSummary = action.payload.data || action.payload;
      })
      .addCase(getAttendanceStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      });
  },
});

export const { clearAttendanceError, addRealtimeAttendance } = attendanceSlice.actions;
export default attendanceSlice.reducer;
